// Core Development Requests — ask for functionality the Sunday State suite
// doesn't have yet: HQ, the Hub, the kiosk, MRP, the storefronts — one intake
// for all of it, because the person with the need shouldn't have to know which
// codebase it lands in.
//
// The operational sibling of the Suggestion Box: attributed always (a request
// needs follow-up questions), and every ask shows where it stands on the same
// ladder development actually moves on. The list of YOUR requests below the
// form is the reason to trust the form — see AnsweredSuggestions for the
// lesson: a submit box with no evidence anything went anywhere stops being
// used, and takes the next channel down with it.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils";
import {
  REQUEST_APPS, REQUEST_AREAS, STATUS_LABEL,
  type DevRequestFacts, type DevRequestStatus,
} from "@/lib/devrequests/request-loop";

const STATUS_PILL: Record<string, string> = {
  new: "pill", considering: "pill", planned: "pill-mint",
  in_progress: "pill-warn", shipped: "pill-ok", declined: "pill-bad",
};

export function DevRequests() {
  const { employee } = useAuth();
  const [mine, setMine] = useState<DevRequestFacts[]>([]);
  const [title, setTitle] = useState("");
  const [app, setApp] = useState("");
  const [area, setArea] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const { data } = await supabase.from("hq_dev_requests")
      .select("*").eq("requester_id", employee.id)
      .order("created_at", { ascending: false });
    setMine((data as DevRequestFacts[]) ?? []);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employee || !title.trim() || !content.trim()) return;
    setBusy(true); setError(null); setInfo(null);
    const { error } = await supabase.from("hq_dev_requests").insert({
      requester_id: employee.id,
      app: app || null,
      area: area || null,
      title: title.trim(),
      content: content.trim(),
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setInfo("✓ Request submitted. You'll be notified when it moves.");
    setTitle(""); setApp(""); setArea(""); setContent("");
    await load();
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Core Dev Requests 🛠️</h1>
      <p className="text-sm text-gray2">
        Something our software should do but can't yet — in HQ, the Hub, the kiosk, MRP, or
        either storefront? Describe the job you're trying to get done. Requests carry your name
        so we can ask follow-up questions, and you'll see the status here as it moves.
      </p>
      {info && <div className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{info}</div>}
      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

      <form onSubmit={submit} className="card space-y-4">
        <div><label className="label">What should it be able to do? *</label>
          <input required maxLength={160} className="input" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Send invoices for several orders at once" /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className="label">Which app</label>
            <select className="input" value={app} onChange={e => setApp(e.target.value)}>
              <option value="">—</option>
              {REQUEST_APPS.map(a => <option key={a}>{a}</option>)}
            </select></div>
          <div><label className="label">Company function</label>
            <select className="input" value={area} onChange={e => setArea(e.target.value)}>
              <option value="">—</option>
              {REQUEST_AREAS.map(a => <option key={a}>{a}</option>)}
            </select></div>
        </div>
        <div><label className="label">The details *</label>
          <textarea required rows={5} className="input" value={content} onChange={e => setContent(e.target.value)}
            placeholder="What are you doing by hand today? What does it block? Who else hits this?" /></div>
        <div className="flex justify-end"><button className="btn" disabled={busy || !title.trim() || !content.trim()}>Submit request</button></div>
      </form>

      {mine.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">Your requests</h2>
          {mine.map(r => (
            <div key={r.id} className="card">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className={`pill ${STATUS_PILL[r.status] ?? ""} text-xs`}>{STATUS_LABEL[r.status as DevRequestStatus] ?? r.status}</span>
                {r.app && <span className="pill text-xs">{r.app}</span>}
                {r.area && <span className="pill pill-mint text-xs">{r.area}</span>}
                <span className="text-xs text-gray2">{fmtDate(r.created_at)}</span>
              </div>
              <p className="text-sm font-medium">{r.title}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray2">{r.content}</p>
              {r.admin_response && (
                <div className="mt-2 rounded-lg bg-offwhite px-3 py-2 text-sm">
                  <span className="font-medium">Response: </span>{r.admin_response}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
